// ============================================================================
// LogiNext's embeddable tracking page, built in the browser.
//
// Their docs: `track/#/order?ordno=<orderNo>&aid=<iFrame token>` — keyed on an ORDER number, with
// the token in the URL as `aid` and WITHOUT the "BASIC " prefix LogiNext displays it with. `ordno`
// is our `provider_ref`: the reference we assign and they echo back.
//
// THE TOKEN IS IN THE FRONTEND BUNDLE. That is a deliberate trade, and it has two consequences
// worth knowing:
//   - It ships to every visitor of the app, not only to people who open this page. An iframe URL is
//     readable in the DOM regardless, so a dashboard user could always see it — but a bundled
//     variable is broader than that.
//   - The token EXPIRES (LogiNext issues it for ~15 days), so rotating it now means a frontend
//     rebuild and redeploy, not a settings change.
// The backend can also mint this URL (/oms/logistics/tracking-embed/, from LOGINEXT_IFRAME_TOKEN in
// the credentials file), which avoids both. `trackingEmbedUrl` returns null when no bundled token is
// set, and the caller falls back to that endpoint — so switching is a matter of unsetting the env var.
// ============================================================================

const TRACK_BASE = "https://api.loginextsolutions.com";

// WHICH VARIANT — `trackall`, and this is not a guess. Their tracking app chooses its backend from
// the URL itself:
//
//     trackUrlType = localStorage["trackUrlType"] || (href.includes("trackall") ? "ALLMILE" : "FMLM")
//
// On `/track/` it calls `/ShipmentApp/shipment/fmlm/get/details`, which answers 405 Method Not
// Allowed for our orders — that is the "something went wrong" the frame showed. On `/trackall/` it
// calls `/ShipmentApp/middlemile/shipment/order/iframe/details`, which returns our orders in full
// (verified against a real order with this token). We book through their MIDDLEMILE API, so the
// middlemile/allmile side is the one that knows our shipments.
//
// The user-type param name follows the same split: `type` on track, `userType` on trackall.
const TRACK_PATH = process.env.REACT_APP_LOGINEXT_TRACK_PATH || "trackall";
const USER_TYPE_PARAM = TRACK_PATH === "trackall" ? "userType" : "type";

// LogiNext shows the token as `BASIC <uuid>`; the URL wants the bare value.
const bareToken = (): string => {
  const raw = (process.env.REACT_APP_LOGINEXT_IFRAME_TOKEN || "").trim();
  return raw.toUpperCase().startsWith("BASIC ") ? raw.slice(6).trim() : raw;
};

export interface TrackingEmbedArgs {
  // LogiNext's `ordno` — our `provider_ref`, the reference we assign and they echo back.
  orderNo?: string | null;
  // Their `orderReferenceId` — our `awb_no`, which is where we store the id LOGINEXT minted
  // (see config.order_web_url). The docs treat the two as alternatives: whichever their tracking
  // service actually indexes is the one that resolves, so both are supported here.
  orderReferenceId?: string | null;
  // FM / LM / ALLMILE — End-to-End account only, ignored by the standard one.
  orderType?: string | null;
}

// The embeddable tracking URL, or null when no token is bundled or neither identifier is known.
// `ADMIN` because this is an internal ops screen — CUSTOMER hides the operational detail.
export const trackingEmbedUrl = (args: TrackingEmbedArgs | string): string | null => {
  const a: TrackingEmbedArgs = typeof args === "string" ? { orderNo: args } : args;
  const token = bareToken();
  if (!token) return null;

  const params = new URLSearchParams();
  // Prefer the reference LogiNext minted; fall back to the one we assigned. The param is
  // `referenceId` — their DOCS say `orderReferenceId`, but the shipped bundle reads `referenceId`
  // (`E.referenceId` off iFrameUserAccessInfo), and the code is what runs.
  if (a.orderReferenceId) params.set("referenceId", a.orderReferenceId);
  else if (a.orderNo) params.set("ordno", a.orderNo);
  else return null;

  params.set("aid", token);
  params.set(USER_TYPE_PARAM, "ADMIN");
  if (TRACK_PATH === "trackall" && a.orderType) params.set("shipmentOrderTypeCd", a.orderType);

  // A HASH route: the query belongs after `#/order`, not before it — their parser takes
  // `location.hash`, splits on "?" and drops the first piece. It then splits on "&" and "=" WITHOUT
  // decoding, so a value carrying "&", "=" or a space would reach them mangled. Ours (an order ref
  // and a UUID) carry none, but that is why nothing arbitrary should be put in this URL.
  return `${TRACK_BASE}/${TRACK_PATH}/#/order?${params.toString()}`;
};
